import * as React from 'react';
import { insightsRequest, insightsError } from 'Src/helpers/service/sltInsights';
import './SltStudents.scss';
const num = v => (v == null ? '—' : Number(v).toLocaleString('en-GB'));
const tiers = ['Not yet', 'Occasional', 'Regular', 'Committed'];
const escape = v =>
  '"' +
  String(v ?? '')
    .replace(/^[\s]*[=+@-]/, "'$&")
    .replace(/"/g, '""') +
  '"';
function download(name, content) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' })),
    a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function StudentTags({ profile }: { profile: any }) {
  return (
    <span className="slsu-tags">
      {[
        ['pupilsPremium', 'PP'],
        ['freeSchoolMeals', 'FSM'],
        ['specialEducationNeeds', 'SEND']
      ]
        .filter(([key]) => profile?.[key] === true)
        .map(([key, label]) => (
          <span key={key}>{label}</span>
        ))}
    </span>
  );
}
export function StudentTier({ student }: { student: any }) {
  const label = student.stopped
    ? 'Stopped'
    : student.tier == null
    ? student.participationCount || student.sessions
      ? 'Taken part'
      : 'Not yet'
    : tiers[student.tier];
  return (
    <span className={'slsu-tier ' + (student.stopped ? 'stopped' : '')}>
      <i
        className={
          student.tier == null && (student.participationCount || student.sessions)
            ? 'unknown'
            : 'tier' + (student.tier || 0)
        }
      />
      {label}
    </span>
  );
}
export function attendedDate(value, cutoff, timezone = 'UTC') {
  if (!value) return '—';
  const at = new Date(value),
    current = new Date(cutoff),
    startYear = current.getUTCFullYear() - (current.getUTCMonth() < 8 ? 1 : 0),
    includeYear = at < new Date(Date.UTC(startYear, 8, 1));
  return at.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(includeYear ? { year: 'numeric' as const } : {}),
    timeZone: timezone
  });
}
export function StudentsContent({
  user,
  query,
  params,
  data,
  list,
  onChange,
  onStudent,
  onSetup,
  onReport,
  onMessage
}: {
  user: any;
  query: any;
  params: URLSearchParams;
  data: any;
  list: any;
  onChange: (q: any) => void;
  onStudent: (id: string) => void;
  onSetup: () => void;
  onReport: () => void;
  onMessage: (value: any) => void;
}) {
  const [selected, setSelected] = React.useState<any>({}),
    [all, setAll] = React.useState(false),
    [busy, setBusy] = React.useState(false),
    [error, setError] = React.useState('');
  const selectionKey = JSON.stringify({ ...query, page: null, snapshotId: null });
  React.useEffect(() => {
    setSelected({});
    setAll(false);
    setError('');
  }, [selectionKey]);
  const meta = list?.meta || {
      counts: {},
      years: [],
      groups: [],
      tiersReady: false,
      stoppedReady: false,
      knownPP: false
    },
    segment = params.get('segment') || 'notyet',
    n = all ? list?.total : Object.keys(selected).length,
    rows = list?.items || [],
    total = list?.total,
    minimum = meta.minimum || 10;
  const clear = () => {
    setSelected({});
    setAll(false);
  };
  const select = (row, checked) => {
    setAll(false);
    setSelected(current => {
      const next = { ...current };
      if (checked) next[row.id] = row;
      else delete next[row.id];
      return next;
    });
  };
  const groups = [
    ['notyet', 'Not yet taken part'],
    ['stopped', 'Stopped taking part'],
    ['ppnot', 'PP not reached'],
    ['occasional', 'Occasional only, KS4'],
    ['all', 'All students']
  ];
  const date = value => attendedDate(value, data.sourceCutoffAt, data.timezone);
  const sort = key =>
    onChange({ sort: key, direction: list?.sort === key && list?.direction === 'asc' ? 'desc' : 'asc', page: 1 });
  const head = (key, label) => (
    <button className="sli-link" onClick={() => sort(key)}>
      {label}
      {list?.sort === key && <span className="slsu-sort">{list.direction === 'desc' ? '▼' : '▲'}</span>}
    </button>
  );
  const exportRows = async () => {
    setBusy(true);
    setError('');
    try {
      if (all) {
        const value = await insightsRequest(user, 'export', query);
        download(value.filename, value.csv);
      } else {
        const content = [
          'Student,Year,Form,Sessions,Last attended',
          ...Object.keys(selected).map(id => {
            const row = selected[id];
            return [
              [row.firstName, row.lastName].join(' '),
              row.groupLabel,
              row.profile.form,
              row.sessions,
              row.lastAttended
            ]
              .map(escape)
              .join(',');
          })
        ].join('\n');
        download('selected-students.csv', content);
      }
    } catch (e) {
      setError(insightsError(e));
    } finally {
      setBusy(false);
    }
  };
  const uncomputable = list?.available === false;
  return (
    <div className="slsu-content">
      <div className="slsu-segments">
        {groups.map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={segment === key ? 'on' : ''}
            onClick={() => onChange({ segment: key, measure: 'participation', page: 1, sort: null, direction: null })}
          >
            <span>{label}</span>
            <b className={meta.counts[key] == null ? 'unavailable' : ''}>{num(meta.counts[key])}</b>
            {key === 'ppnot' && !meta.knownPP ? (
              <small>PP not in MIS export</small>
            ) : key === 'stopped' && !meta.stoppedReady && meta.tiersReady ? (
              <small>{meta.stoppedReason || 'Needs comparable participation history'}</small>
            ) : (key === 'stopped' && !meta.stoppedReady) || (key === 'occasional' && !meta.tiersReady) ? (
              <small>
                Needs half-terms ·{' '}
                <span
                  role="link"
                  tabIndex={0}
                  onClick={e => {
                    e.stopPropagation();
                    onSetup();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') onSetup();
                  }}
                >
                  Set up
                </span>
              </small>
            ) : null}
          </button>
        ))}
      </div>
      <section className="slo-card slsu-tableCard">
        {n > 0 ? (
          <div className="slsu-selection">
            <b>{num(n)} selected</b>
            <button className="sli-button sli-primary" onClick={() => onMessage({ audience: 'form tutors', count: n })}>
              Message form tutors
            </button>
            <button className="sli-button" onClick={() => onMessage({ audience: 'parents', count: n })}>
              Message parents
            </button>
            <button
              className="sli-button"
              onClick={onReport}
              title="Generate a participation summary for the current filtered group"
            >
              Group summary
            </button>
            <details>
              <summary>Other downloads</summary>
              <button className="sli-button" disabled={busy} onClick={exportRows}>
                Selected students (CSV)
              </button>
            </details>
            <button className="sli-link" onClick={clear}>
              Clear
            </button>
            {!all && Object.keys(selected).length === rows.length && total > rows.length && (
              <button className="sli-link" onClick={() => setAll(true)}>
                Select all {num(total)} in this group
              </button>
            )}
          </div>
        ) : (
          <div className="slsu-filterbar">
            <div>
              <label>Year</label>
              <div className="sli-density">
                {['', ...meta.years].map(key => (
                  <button
                    key={key}
                    className={(params.get('tableYear') || '') === key ? 'on' : ''}
                    onClick={() => onChange({ tableYear: key || null, page: 1 })}
                  >
                    {key ? 'Y' + key : 'All'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label>Group</label>
              <div className="sli-density">
                <button
                  className={!params.get('tableGroup') ? 'on' : ''}
                  onClick={() => onChange({ tableGroup: null, page: 1 })}
                >
                  All
                </button>
                {meta.groups
                  .filter(group => group.recorded > 0)
                  .map(group => (
                    <button
                      key={group.label}
                      className={params.get('tableGroup') === group.filter ? 'on' : ''}
                      disabled={group.disabled}
                      title={group.disabled ? 'Fewer than ' + minimum + ' students' : undefined}
                      onClick={() => onChange({ tableGroup: group.filter, page: 1 })}
                    >
                      {group.label}
                    </button>
                  ))}
              </div>
            </div>
            <span>{segment === 'never' ? 'No recorded participation · ' : segment === 'retained' ? 'Retained participants · ' : ''}{num(total)} students</span>
          </div>
        )}
        {!meta.tiersReady && (
          <p className="slsu-tierHint">
            Tiers aren't set up, so Occasional / Regular / Committed aren't shown ·{' '}
            <button className="sli-link" onClick={onSetup}>
              Set up →
            </button>
          </p>
        )}
        {error && <p role="alert">{error}</p>}
        <div className="slsu-tableWrap">
          <table className="slsu-table">
            <colgroup>
              {['56px', '26%', '13%', '17%', '16%', '10%', null].map((width, i) => (
                <col key={i} style={width ? { width } : {}} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Select this page"
                    checked={!!rows.length && (all || rows.every(row => !!selected[row.id]))}
                    disabled={!rows.length}
                    onChange={e => {
                      setAll(false);
                      setSelected(e.target.checked ? rows.reduce((all, row) => ({ ...all, [row.id]: row }), {}) : {});
                    }}
                  />
                </th>
                <th>{head('name', 'Student')}</th>
                <th>{head('year', 'Year · form')}</th>
                <th>Groups</th>
                <th>How often</th>
                <th>{head('sessions', 'Sessions')}</th>
                <th>{head('recent', 'Last attended')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(student => (
                <tr
                  key={student.id}
                  role="button"
                  tabIndex={0}
                  className={all || selected[student.id] ? 'selected' : ''}
                  aria-label={'Open student ' + student.firstName + ' ' + student.lastName}
                  onClick={() => onStudent(student.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onStudent(student.id);
                    }
                  }}
                >
                  <td onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={'Select ' + student.firstName + ' ' + student.lastName}
                      checked={all || !!selected[student.id]}
                      onChange={e => select(student, e.target.checked)}
                    />
                  </td>
                  <td>
                    <b>
                      {student.firstName} {student.lastName}
                    </b>
                  </td>
                  <td>
                    {student.groupLabel}
                    {student.profile.form ? ' · ' + student.profile.form : ''}
                  </td>
                  <td>
                    <StudentTags profile={student.profile} />
                  </td>
                  <td>
                    <StudentTier student={student} />
                  </td>
                  <td
                    title={`${student.sessions} session participation records · ${student.attendedSessions ||
                      0} confirmed attendances`}
                  >
                    {student.sessions}
                  </td>
                  <td>{date(student.lastAttended)}</td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={7} className="slsu-empty">
                    {uncomputable ? (
                      <>
                        <p>{list?.reason || 'This group needs teaching half-terms. Set them up in Thresholds & alerts.'}</p>
                        {!meta.tiersReady && <button className="sli-button" onClick={onSetup}>
                          Set up
                        </button>}
                      </>
                    ) : list?.suppressed ? (
                      'Fewer than ' + minimum + ' students — hidden'
                    ) : (
                      'No students match. Try a wider year or group.'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="slsu-footer">
          <span>
            Showing {rows.length} of {num(total)}
          </span>
          <div>
            <button
              className="sli-button"
              disabled={(list?.page || 1) <= 1}
              onClick={() => onChange({ page: (list?.page || 1) - 1 })}
            >
              Previous
            </button>
            <button
              className="sli-button"
              disabled={total == null || (list?.page || 1) * (list?.limit || 25) >= total}
              onClick={() => onChange({ page: (list?.page || 1) + 1 })}
            >
              Next
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
export function StudentDrawer({
  student,
  data,
  onActivity,
  onMessage
}: {
  student: any;
  data: any;
  onActivity: (activity: any) => void;
  onMessage: (value: any) => void;
}) {
  const date = value => attendedDate(value, data.sourceCutoffAt, data.timezone),
    weekly = student.weekly || [],
    max = Math.max(1, ...weekly.map(w => w.present || 0)),
    line = weekly
      .map((w, i) =>
        w.present == null
          ? ''
          : (i && weekly[i - 1].present != null ? 'L' : 'M') +
            (2 + (i * 456) / Math.max(1, weekly.length - 1)) +
            ' ' +
            (57 - (w.present * 54) / max)
      )
      .join(' ');
  return (
    <div className="slsu-drawer">
      <div className="slsu-drawerBody">
        <h2>
          {student.firstName} {student.lastName}
        </h2>
        <p>
          {student.groupLabel}
          {student.profile.form ? ' · ' + student.profile.form : ''}
        </p>
        <div className="slsu-drawerTags">
          <StudentTags profile={student.profile} />
          <StudentTier student={student} />
        </div>
        <div className="slsu-mini">
          {[
            ['Sessions', num(student.sessions)],
            ['Clubs / teams', num(student.activities?.length)],
            ['Last attended', date(student.lastAttended)]
          ].map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <b>{value}</b>
            </div>
          ))}
        </div>
        <h3>Activities</h3>
        {(student.activities || []).map(activity => (
          <button className="slsu-activity" key={activity.id} onClick={() => onActivity(activity)}>
            <b>{activity.name}</b>
            <span>{activity.sessions} sessions ›</span>
          </button>
        ))}
        {!student.activities?.length && <p className="sli-muted">No participation recorded in this period.</p>}
        <h3>Attendance by week</h3>
        <div className="slsu-weekly">
          {weekly.some(w => w.present != null) ? (
            <svg viewBox="0 0 460 60" width="460" height="60" role="img" aria-label="Confirmed attendance by week">
              <path d={line} fill="none" stroke="var(--blue-500)" strokeWidth="2.5" />
              {weekly
                .filter(w => w.present != null)
                .map((w, i) => (
                  <circle
                    key={w.at}
                    cx={2 + (i * 456) / Math.max(1, weekly.length - 1)}
                    cy={57 - (w.present * 54) / max}
                    r="2"
                    fill="var(--blue-500)"
                  >
                    <title>
                      {w.at.slice(0, 10)} · {w.present} sessions
                    </title>
                  </circle>
                ))}
            </svg>
          ) : (
            <span className="sli-muted">Attendance not recorded; participation is shown above.</span>
          )}
        </div>
      </div>
      <footer className="slsu-drawerFooter">
        <button className="sli-button sli-primary" onClick={() => onMessage({ audience: 'form tutor', student })}>
          Message form tutor
        </button>
        <button className="sli-button" onClick={() => onMessage({ audience: 'parents', student })}>
          Message parents
        </button>
        <button className="sli-button" onClick={() => onMessage({ audience: 'form tutor', student, suggestion: true })}>
          Suggest a club
        </button>
      </footer>
    </div>
  );
}
